/**
 * Posts mode (§5.1 #25, §5.4, §12.3, PRD Posts mode).
 *
 * TanStack Table renders the columns declared in `packages/shared/columns.ts`; TanStack Virtual
 * virtualizes rows at a fixed height and the scrollbar is sized from the server's `total`. Data
 * arrives as offset windows of 200 around the viewport, fetched with `keepPreviousData` so
 * scrolling and filter changes never blank the grid (§5.4).
 *
 * Sorting is server-side: a header click writes the same `sort` state the header's Sort control
 * writes while this mode is mounted (§12.3 — one source of truth per tab; Feed keeps its own
 * `feedSort`). Column visibility and order come from the global `columns:posts` UI state and are
 * reorderable by dragging a header.
 *
 * Since the redesign the grid is edge-to-edge fullscreen: search, Columns and Export live in the
 * flat view header (which shares this file's cache-backed `useColumnLayout`), and the column
 * header row sticks at `--table-sticky-top` inside the grid's own scroll container.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { keepPreviousData, useQueries } from '@tanstack/react-query';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  MAX_POSTS_LIMIT,
  POSTS_COLUMNS,
  getPostsColumn,
  type PostRowDTO,
  type PostsColumnId,
  type PostsQuery,
  type SubjectType,
  type ViewFilterParams,
  type ViewSummaryResponse,
} from '@lw/shared';
import { getViewPosts } from '../../../api/views';
import { QK } from '../../../api/queryKeys';
import { EmptyState, ErrorState, HoverTip, SortArrowIcon, Spinner, cx } from '../../../components';
import { formatDate } from '../../../lib/datetime';
import { formatInteger, pluralize } from '../../../lib/format';
import { usePostPreview } from '../PreviewContext';
import type { ViewSort } from '../viewState';
import { buildPostsColumns, columnWidth } from './postsColumns';
import { useColumnLayout } from './useColumnLayout';

const ROW_HEIGHT = 40;
const PAGE = MAX_POSTS_LIMIT;
const DRAG_THRESHOLD_PX = 4;
/** Hover intent for column-header hints — long enough that sweeping the row shows nothing. */
const HEADER_TIP_DELAY_MS = 180;

export type PostsModeProps = {
  subjectType: SubjectType;
  id: number;
  params: Partial<ViewFilterParams>;
  sort: ViewSort;
  onSortChange: (next: ViewSort) => void;
  enabled: boolean;
  timezone: string;
  /** `settings.mediaEnabled` — decides whether a preview-cell thumb loads at all (§9.3). */
  mediaEnabled: boolean;
  hasFilters: boolean;
  onClearFilters: () => void;
  /** Feeds the footer — Posts mode replaces the header sub-row with it (redesign). */
  summary: ViewSummaryResponse | undefined;
};

export function PostsMode({
  subjectType,
  id,
  params,
  sort,
  onSortChange,
  enabled,
  timezone,
  mediaEnabled,
  hasFilters,
  onClearFilters,
  summary,
}: PostsModeProps) {
  const layout = useColumnLayout();
  const { openPreview } = usePostPreview();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [windowIds, setWindowIds] = useState<number[]>([0]);

  const isGroup = subjectType === 'group';

  const visibleColumns = useMemo(
    () =>
      layout.order.filter((columnId) => {
        if (layout.hidden.has(columnId)) return false;
        return getPostsColumn(columnId).groupOnly ? isGroup : true;
      }),
    [layout.order, layout.hidden, isGroup],
  );

  /* --------------------------------------------------------------- windows */

  const queries = useQueries({
    queries: windowIds.map((windowId) => {
      const query: Partial<PostsQuery> = {
        ...params,
        sort: sort.key,
        dir: sort.dir,
        offset: windowId * PAGE,
        limit: PAGE,
      };
      return {
        queryKey: QK.viewPosts(subjectType, id, query),
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          getViewPosts(subjectType, id, query, signal),
        enabled: enabled && layout.ready,
        placeholderData: keepPreviousData,
      };
    }),
  });

  // Folds in every window's identity and freshness, so the row map recomputes exactly when a
  // window resolves, errors or is replaced.
  const signature = `${windowIds.join(',')}#${queries
    .map((query) => `${query.status}:${query.dataUpdatedAt}:${query.errorUpdatedAt}`)
    .join(',')}`;

  const { rows, indexOfRow, total, isPending, isError, error } = useMemo(() => {
    const byIndex: [number, PostRowDTO][] = [];
    let resolvedTotal: number | null = null;
    for (let i = 0; i < windowIds.length; i += 1) {
      const result = queries[i];
      if (result?.data === undefined) continue;
      resolvedTotal ??= result.data.total;
      const base = (windowIds[i] ?? 0) * PAGE;
      result.data.rows.forEach((row, offset) => byIndex.push([base + offset, row]));
    }
    byIndex.sort((a, b) => a[0] - b[0]);
    const map = new Map<number, number>();
    byIndex.forEach(([absoluteIndex], position) => map.set(absoluteIndex, position));
    return {
      rows: byIndex.map(([, row]) => row),
      indexOfRow: map,
      total: resolvedTotal ?? 0,
      isPending: queries.some((q) => q.isPending),
      isError: queries.some((q) => q.isError),
      error: queries.find((q) => q.isError)?.error,
    };
  }, [signature]);

  /* ----------------------------------------------------------------- table */

  const columns = useMemo(
    () => buildPostsColumns({ timezone, mediaEnabled }),
    [timezone, mediaEnabled],
  );

  const columnVisibility = useMemo(() => {
    const visible = new Set<string>(visibleColumns);
    return Object.fromEntries(POSTS_COLUMNS.map((column) => [column.id, visible.has(column.id)]));
  }, [visibleColumns]);

  const table = useReactTable({
    data: rows,
    columns,
    state: { columnOrder: layout.order, columnVisibility },
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
  });

  const tableRows = table.getRowModel().rows;
  const leafColumns = table.getVisibleLeafColumns();

  /* ------------------------------------------------------------ virtualizer */

  const virtualizer = useVirtualizer({
    count: total,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });

  const virtualItems = virtualizer.getVirtualItems();

  // Fetch exactly the window(s) the viewport touches (§5.4).
  const first = virtualItems[0]?.index ?? 0;
  const last = virtualItems[virtualItems.length - 1]?.index ?? 0;
  const neededWindows = useMemo(() => {
    const from = Math.floor(first / PAGE);
    const to = Math.floor(last / PAGE);
    return from === to ? [from] : [from, to];
  }, [first, last]);

  if (neededWindows.join(',') !== windowIds.join(',')) {
    // Render-phase state sync (React's "adjusting state on prop change" pattern): the virtualizer
    // reports its range during render, and the queries must follow it in the same commit.
    setWindowIds(neededWindows);
  }

  /* ---------------------------------------------------- header interactions */

  const headerRefs = useRef(new Map<string, HTMLDivElement | null>());
  const pinned = layout.pinned;
  const dragState = useRef<{ id: PostsColumnId; startX: number; moved: boolean } | null>(null);
  const [drag, setDrag] = useState<{ id: PostsColumnId; overId: PostsColumnId | null } | null>(null);

  // Header hints are hover-intent only: any pointer press cancels them, so neither a click-sort
  // nor a drag-reorder ever happens with a hint floating over the row.
  const [headerTip, setHeaderTip] = useState<PostsColumnId | null>(null);
  const tipTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelHeaderTip = useCallback(() => {
    if (tipTimer.current !== null) clearTimeout(tipTimer.current);
    tipTimer.current = null;
    setHeaderTip(null);
  }, []);
  const scheduleHeaderTip = useCallback((columnId: PostsColumnId) => {
    if (tipTimer.current !== null) clearTimeout(tipTimer.current);
    tipTimer.current = setTimeout(() => setHeaderTip(columnId), HEADER_TIP_DELAY_MS);
  }, []);
  useEffect(() => () => {
    if (tipTimer.current !== null) clearTimeout(tipTimer.current);
  }, []);

  const headerTipRef = useRef<HTMLElement | null>(null);
  headerTipRef.current = headerTip === null ? null : headerRefs.current.get(headerTip) ?? null;

  const toggleSort = useCallback(
    (columnId: PostsColumnId) => {
      const column = getPostsColumn(columnId);
      if (!column.sortable) return;
      const key = columnId as ViewSort['key'];
      onSortChange(
        sort.key === key
          ? { key, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
          : { key, dir: column.kind === 'date' || column.kind === 'metric' || column.kind === 'multiplier' ? 'desc' : 'asc' },
      );
    },
    [onSortChange, sort],
  );

  const onHeaderPointerDown = (event: ReactPointerEvent<HTMLDivElement>, columnId: PostsColumnId) => {
    cancelHeaderTip();
    if (event.button !== 0) return;
    dragState.current = { id: columnId, startX: event.clientX, moved: false };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onHeaderPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const state = dragState.current;
    if (state === null) return;
    // The pinned column is not draggable. Bail *before* the threshold, so `moved` stays false and
    // pointer-up takes the click branch (no drag styling, and a click-sort would still work).
    if (state.id === pinned) return;
    if (!state.moved && Math.abs(event.clientX - state.startX) < DRAG_THRESHOLD_PX) return;
    state.moved = true;
    let overId: PostsColumnId | null = null;
    // While scrolled, the columns underneath the sticky pinned header still contain the pointer,
    // so its rect is a dead zone checked first — not merely skipped in the scan below.
    const pinnedHeader = pinned === null ? null : (headerRefs.current.get(pinned) ?? null);
    const pinnedRect = pinnedHeader?.getBoundingClientRect() ?? null;
    const overPinned =
      pinnedRect !== null && event.clientX >= pinnedRect.left && event.clientX <= pinnedRect.right;
    if (!overPinned) {
      for (const [candidate, element] of headerRefs.current) {
        if (element === null || candidate === pinned) continue;
        const rect = element.getBoundingClientRect();
        if (event.clientX >= rect.left && event.clientX <= rect.right) {
          overId = candidate as PostsColumnId;
          break;
        }
      }
    }
    setDrag({ id: state.id, overId });
  };

  const onHeaderPointerUp = (event: ReactPointerEvent<HTMLDivElement>, columnId: PostsColumnId) => {
    const state = dragState.current;
    dragState.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (state !== null && !state.moved) {
      setDrag(null);
      toggleSort(columnId);
      return;
    }
    const target = drag?.overId ?? null;
    if (state !== null && target !== null && target !== state.id) {
      const next = layout.order.filter((entry) => entry !== state.id);
      const at = next.indexOf(target);
      next.splice(at < 0 ? next.length : at, 0, state.id);
      layout.setOrder(next);
    }
    setDrag(null);
  };

  /* ------------------------------------------------------------------ render */

  const showEmpty = !isPending && !isError && total === 0;

  return (
    <div className="posts">
      {isError ? (
        <ErrorState
          title="Couldn't load posts"
          error={error}
          onRetry={() => queries.forEach((query) => void query.refetch())}
        />
      ) : null}

      {showEmpty ? (
        <EmptyState
          title={hasFilters ? 'No posts match these filters' : 'No posts in this range'}
          description={
            hasFilters
              ? 'Every stored post was filtered out. Clear the filters to see the full list.'
              : 'Widen the date range, or scrape this subject to collect posts.'
          }
          action={
            hasFilters ? (
              <button type="button" className="btn-primary" onClick={onClearFilters}>
                Clear filters
              </button>
            ) : null
          }
        />
      ) : null}

      {!showEmpty && !isError ? (
        <>
          <div className="posts__grid" ref={scrollRef}>
            <div className="posts__inner">
              <div className="pt-row pt-row--head" role="row">
                {leafColumns.map((column) => {
                  const columnId = column.id as PostsColumnId;
                  const def = getPostsColumn(columnId);
                  const active = sort.key === columnId;
                  const header = table
                    .getHeaderGroups()[0]
                    ?.headers.find((candidate) => candidate.column.id === column.id);
                  return (
                    <div
                      key={column.id}
                      role="columnheader"
                      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                      className={cx(
                        'pt-cell pt-cell--head',
                        `pt-cell--${def.align}`,
                        columnId === 'preview' && 'pt-cell--flex',
                        columnId === pinned && 'pt-cell--pinned',
                        def.sortable && 'pt-cell--sortable',
                        drag?.id === columnId && 'pt-cell--dragging',
                        drag?.overId === columnId && drag.id !== columnId && 'pt-cell--dropbefore',
                      )}
                      style={columnId === 'preview' ? undefined : { width: columnWidth(columnId) }}
                      ref={(element) => {
                        headerRefs.current.set(column.id, element);
                      }}
                      onPointerDown={(event) => onHeaderPointerDown(event, columnId)}
                      onPointerMove={onHeaderPointerMove}
                      onPointerUp={(event) => onHeaderPointerUp(event, columnId)}
                      onMouseEnter={() => scheduleHeaderTip(columnId)}
                      onMouseLeave={cancelHeaderTip}
                    >
                      <span className="pt-cell__label">
                        {header === undefined
                          ? def.label
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </span>
                      {def.sortable && active ? (
                        <span className="pt-cell__arrow" aria-hidden="true">
                          <SortArrowIcon dir={sort.dir} />
                        </span>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              <div className="posts__body" style={{ height: virtualizer.getTotalSize() }}>
                {virtualItems.map((item) => {
                  const position = indexOfRow.get(item.index);
                  const row = position === undefined ? undefined : tableRows[position];
                  return (
                    <div
                      key={item.key}
                      className={cx('pt-row', row === undefined && 'pt-row--skeleton')}
                      role="row"
                      style={{ height: item.size, transform: `translateY(${item.start}px)` }}
                      onClick={(event) => {
                        // A tooltip trigger inside a cell is not a row activation.
                        if (event.target instanceof Element && event.target.closest('.tip') !== null) {
                          return;
                        }
                        if (row !== undefined) {
                          openPreview(
                            row.original.id,
                            rows.map((entry) => entry.id),
                          );
                        }
                      }}
                    >
                      {row === undefined
                        ? leafColumns.map((column) => (
                            <div
                              key={column.id}
                              className={cx(
                                'pt-cell',
                                column.id === 'preview' && 'pt-cell--flex',
                                column.id === pinned && 'pt-cell--pinned',
                              )}
                              style={
                                column.id === 'preview'
                                  ? undefined
                                  : { width: columnWidth(column.id as PostsColumnId) }
                              }
                            >
                              <span className="skeleton" />
                            </div>
                          ))
                        : row.getVisibleCells().map((cell) => {
                            const columnId = cell.column.id as PostsColumnId;
                            const def = getPostsColumn(columnId);
                            return (
                              <div
                                key={cell.id}
                                role="cell"
                                className={cx(
                                  'pt-cell',
                                  `pt-cell--${def.align}`,
                                  columnId === 'preview' && 'pt-cell--flex',
                                  columnId === pinned && 'pt-cell--pinned',
                                )}
                                style={
                                  columnId === 'preview' ? undefined : { width: columnWidth(columnId) }
                                }
                              >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </div>
                            );
                          })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <HoverTip
            open={headerTip !== null}
            anchorRef={headerTipRef}
            text={headerTip === null ? '' : getPostsColumn(headerTip).description}
          />
        </>
      ) : null}

      {isPending && !showEmpty ? (
        <div className="row muted posts__loading">
          <Spinner size="sm" />
          Fetching rows…
        </div>
      ) : null}

      {/* Last child of the 100dvh column: the grid scrolls, this stays pinned to the bottom. */}
      <div className="posts__footer">
        <span>
          {summary === undefined
            ? ''
            : `${pluralize(summary.postCount, 'post')} stored · ${
                summary.lastScrapedAt === null
                  ? 'Never scraped'
                  : `Last scraped ${formatDate(summary.lastScrapedAt, timezone)}`
              }`}
        </span>
        <span>{isPending ? 'Loading…' : `${formatInteger(total)} shown`}</span>
      </div>
    </div>
  );
}

export default PostsMode;
